import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import useAgencyProvider, { useAgencyProviderForm } from '@hooks/useAgencyProvider'
import useAgencyProviders from '@hooks/useAgencyProviders'
import useBrandProvider from '@hooks/useBrandProvider'
import useCurrentUser from '@hooks/useCurrentUser'
import useProviderPriceLists from '@hooks/useProviderPriceLists'
import useServices from '@hooks/useServices'

const getToastMessage = (params) => {
  const { entityState, hideAgencyAndBrand, isProviderDirector } = params

  const providerDirectorText = isProviderDirector && hideAgencyAndBrand ? 'Provider Price List' : 'Agency'

  return entityState.id ? `${isProviderDirector ? providerDirectorText
    : 'Provider '} Updated` : `${isProviderDirector ? providerDirectorText : 'Provider'}  Added`
}

const createOrUpdateAgencyProvider = (params) => {
  const {
    isProviderDirector,
    entityState,
    hideAgencyAndBrand,
    props,
    createFn,
    updateFn,
  } = params

  const {
    callbacks: { closeModal, createAgencyProvider, updateAgencyProvider },
  } = props

  const actionFn = entityState.id ? updateFn : createFn
  const toastText = getToastMessage({ entityState, hideAgencyAndBrand, isProviderDirector })

  const agencyProviderPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_agency_provider_account_for_provider: true,
      include_agency_provider_agency: true,
      include_agency_provider_provider_price_list: true,
    },
    toastText,
  }

  if (entityState.id) return updateAgencyProvider(agencyProviderPayload)

  return createAgencyProvider(agencyProviderPayload)
}

const mapResults = (params) => {
  const { agencies } = params
  if (!agencies.length){
    return {}
  }

  const groups = [{
    items: agencies.map((result) => {
      const {
        id,
        title,
      } = result

      return {
        ...result,
        key: id,
        secondaryText: `Agency ID #${id}`,
        text: title,
      }
    }),
    key: 'agencies',
    title: 'Agencies',
  }]

  const mapped = {
    groups,
  }

  return mapped
}

const setAgencyProvider = ({ providerPriceList, setEntityState }) => {
  const { provider_id, id, service_id } = providerPriceList
  setEntityState({ provider_id, provider_price_list_id: id, service_id: service_id || '' })
}

const handleChangeAgencyId = ({ setEntityState, value }) => {
  if (!value){
    setEntityState({
      agency_id: undefined,
      provider_price_list_id: undefined,
      selectedPriceListId: undefined,
      service_id: undefined,
    })
    return
  }

  setEntityState({ agency_id: value })
}

const handleChangeBrandId = ({ setEntityState, value }) => {
  if (!value){
    setEntityState({
      agency_id: undefined,
      brandId: undefined,
      provider_price_list_id: undefined,
      service_id: '',
      selectedPriceListId: undefined,
    })
    return
  }

  setEntityState({ brandId: value })
}

const handleChangePriceListId = ({ setEntityState, value }) => {
  if (!value){
    setEntityState({ provider_price_list_id: undefined, service_id: '', selectedPriceListId: undefined })
    return
  }

  setEntityState({ selectedPriceListId: value })
}

const handleChangeProviderId = ({ value, setEntityState }) => {
  if (!value){
    setEntityState({ provider_id: undefined, provider_price_list_id: undefined })
    return
  }

  setEntityState({ provider_id: value })
}

const handleChangeServiceId = ({ isProviderDirector, value, setEntityState }) => {
  if (isProviderDirector){
    return setEntityState({
      service_id: value,
      provider_price_list_id: undefined,
    })
  }

  return setEntityState({
    service_id: value,
    provider_id: undefined,
    provider_price_list_id: undefined,
  })
}

const useCreateOrEditAgencyProviderModal = (agencyProvider) => {
  const { agency_id } = agencyProvider

  const {
    agency,
    callbacks: {
      createAgencyProvider,
      deleteAgencyProvider,
      updateAgencyProvider,
    },
    creating,
    deleting,
    updating,
  } = useAgencyProvider(agencyProvider)

  const {
    callbacks: { addBrandProvider },
  } = useBrandProvider({ brand_id: agency.brand_id })

  // Agency Provider Form
  const agencyProviderForm = useAgencyProviderForm(agencyProvider)
  const {
    entityState: {
      provider_id,
      service_id,
      selectedPriceListId,
    },
    setEntityState,
  } = agencyProviderForm

  // Get Price List from EntitySelector
  const providerPriceLists = useSelector(reduxState => reduxState.entities.providerPriceLists)
  const providerPriceList = providerPriceLists[selectedPriceListId] || {}

  const { filteredServices } = useServices({
    filters: { assigned_provider_id: provider_id },
    performHttpRequests: !!provider_id,
    requestOptions: { include_service_assigned_provider_ids: true },
  })

  // Price Lists
  const providerPriceListPayload = useProviderPriceLists({
    filters: { provider_id, service_id },
    performHttpRequests: !!provider_id && !!service_id,
  })
  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredProviderPriceLists,
  } = providerPriceListPayload

  // Get list of Providers already assigned to the service so we can exclude from EntitySelector
  const agencyProvidersPayload = useAgencyProviders({
    filters: {
      agency_id,
      service_id,
    },
  })

  const { filteredAgencyProviders } = agencyProvidersPayload
  const assignedProviderIds = filteredAgencyProviders.map(entity => entity.provider_id)

  const firstRun = useRef(true)

  // Set service_id and provider_id when providerPriceList loads
  // Using the ref to ensure it doesn't run on mount and clear service_id
  useEffect(() => {
    if (!firstRun.current && providerPriceList.cache_key){
      setAgencyProvider({ providerPriceList, setEntityState })
    }

    firstRun.current = false
  }, [providerPriceList.id])

  const currentUserPayload = useCurrentUser()

  return {
    agency,
    agencyProviderForm,
    assignedProviderIds,
    callbacks: {
      addBrandProvider,
      createAgencyProvider,
      createOrUpdateAgencyProvider: params => createOrUpdateAgencyProvider({ ...params }),
      deleteAgencyProvider,
      handleChangeAgencyId: targetAgency => handleChangeAgencyId({
        setEntityState,
        value: targetAgency?.id,
      }),
      handleChangeBrandId: value => handleChangeBrandId({
        setEntityState,
        value,
      }),
      handleChangePriceListId: priceList => handleChangePriceListId({ setEntityState, value: priceList?.id }),
      handleChangeProviderId: value => handleChangeProviderId({ value, setEntityState }),
      handleChangeServiceId: (value, isProviderDirector) => handleChangeServiceId({
        isProviderDirector,
        setEntityState,
        value,
      }),
      loadMorePriceLists: loadMore,
      mapResults: params => mapResults({ ...params }),
      updateAgencyProvider,
    },
    canLoadMore,
    creating,
    currentUserPayload,
    deleting,
    filteredProviderPriceLists,
    filteredServices,
    selectedPriceListId,
    updating,
  }
}

export default useCreateOrEditAgencyProviderModal
